import { Box, Button, Stack, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { T } from "../../hooks/useTranslate";
import { useMixPanel } from "../../hooks/useMixPanel";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { createElement, Dispatch, SetStateAction } from "react";
import { PopupAndCardConfig, PopupAndCardState } from "../../types/components/popup";
import { usePopupContext } from "../../hooks/PopupContext";
import { useLanguageContext } from "../../hooks/LanguageContext";
import usePopup from "../../hooks/usePopup";

interface PopupAttributes {
  popupId: string;
  isClosed: boolean;
  isCardForm: boolean;
}

interface AnnouncementProps {
  data: PopupAndCardConfig;
  handlePopupAttributeChange: (attributes: PopupAttributes) => void;
}

interface PopupContextProperties {
  setPopupAndCardState: Dispatch<SetStateAction<PopupAndCardState>>;
}

const Announcement = ({ data, handlePopupAttributeChange }: AnnouncementProps) => {
  const { styles } = data
  const {
    setPopupAndCardState
  } = usePopupContext() as PopupContextProperties
  const { handleDismissPopup } = usePopup()

  const { logEvent } = useMixPanel()
  const navigate = useNavigate()
  // @ts-expect-error untyped auth value
  const { userInfo } = useAuth()
  const { currentLanguage } = useLanguageContext()

  const handleSecondaryCtaClick = async () => {
    logEvent("secondary_cta_clicked", {
      unicity_id: userInfo?.unicityId,
      country_code: userInfo?.countryCode,
      popup_content_id: data?.contentId,
    })

    await handlePopupAttributeChange({
      popupId: data?.contentId,
      isClosed: false,
      isCardForm: true
    })
  }
  // Must be able to handle redirect and non-redirect actions
  const handleMainCtaClick = () => {
    logEvent("announcement_clicked", {
      link: data?.links?.path || data?.links?.external,
      unicity_id: userInfo?.unicityId,
      country_code: userInfo?.countryCode,
      popup_content_id: data?.contentId,
    })
    // For method popup
    if (data?.contentId.includes('method-')) {
      setPopupAndCardState((prev: PopupAndCardState) => {
        const events = prev.popupData
        const index = events.findIndex(item => item.contentId === data.contentId)
        events[index].isCardForm = true
        const qualifiedEventIndex = prev.qualifiedEvents.findIndex(item => item.id === data.contentId)
        prev.qualifiedEvents[qualifiedEventIndex].isCustomFormPopupActive = true

        return { ...prev, popupData: events }
      })
      return
    }

    if (data?.links?.path) {
      navigate(data.links.path)
      handleDismissPopup()
      return
    }

    if (data?.links?.external) {
      window.open(data.links.external, "_blank");
      return
    }
    handleDismissPopup()
  }

  return (
    <Box sx={{ maxWidth: styles.maxWidth, overflow: "hidden", position: "relative" }}>
      <IconButton
        onClick={() => handleDismissPopup()}
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }
        }}
        data-test-id="announcement-close-button"
        aria-label="close announcement"
      >
        <CloseIcon />
      </IconButton>
      <Box sx={{ width: "100%" }}>
        <img
          src={data.resourceLinks[currentLanguage] ? data.resourceLinks[currentLanguage] : data.resourceLinks.main}
          alt="announcement"
          style={{
            width: "100%",
            maxHeight: "430px",
            objectFit: "cover"
          }}
        />
      </Box>
      <Stack
        sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
        px={3}
        pb={5}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            lineHeight: "120%",
            mt: 2
          }}
        >
          <T>{data.text.popupText.titleTextKey}</T>
        </Typography>
        <Stack>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: "12px",
              lineHeight: "150%",
              mt: 2,
              wordBreak: "break-word",
              overflowWrap: "break-word",
              maxWidth: "100%"
            }}
          >
            <T components={
              data.text.popupText.description?.components?.map((htmlTag, idx) => {
                if (htmlTag === "br") {
                  return <br key={idx} />
                }
                return createElement(htmlTag, { key: idx })
              })
            }>
              {data.text.popupText.description.key}
            </T>
          </Typography>
          {data.text?.popupText?.subtext && Object.keys(data.text.popupText.subtext).length > 0 && (
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "150%",
                mt: 2,
                wordBreak: "break-word",
                overflowWrap: "break-word",
                maxWidth: "100%"
              }}
            >
              <T components={
                data.text.popupText.subtext?.components?.map((htmlTag, idx) => {
                  if (htmlTag === "br") {
                    return <br key={idx} />
                  }
                  return createElement(htmlTag, { key: idx })
                })
              }>{data.text.popupText.subtext.key}</T>
            </Typography>
          )}
        </Stack>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 3,
          gap: 2,
          textDecoration: 'none'
        }}>
          {
            data.text?.popupText?.secondaryButtonTextKey && (
              <Button
                data-test-id="secondary-cta-button"
                onClick={handleSecondaryCtaClick}
                sx={{
                  borderRadius: "33px",
                  backgroundColor: "#dddddd",
                  '&:hover': {
                    backgroundColor: "#003764",
                  },
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "12px",
                  lineHeight: "100%",
                  width: "30%",
                  p: 1.5
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "120%",
                    textAlign: "center"
                  }}
                >
                  <T>{data.text.popupText.secondaryButtonTextKey}</T>
                </Typography>
              </Button>
            )
          }
          <Button
            data-test-id="announcement-button"
            onClick={handleMainCtaClick}
            sx={{
              borderRadius: "33px",
              backgroundColor: "#003764",
              '&:hover': {
                backgroundColor: "#003764",
              },
              color: "#FFFFFF",
              fontWeight: 600,
              fontSize: "12px",
              lineHeight: "100%",
              width: "70%",
              p: 1.5
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "120%",
                textAlign: "center"
              }}
            >
              <T>{data.text.popupText.buttonTextKey}</T>
            </Typography>
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}

export default Announcement